import React, { useEffect, useRef, useState, useMemo } from 'react';
import './sideBar.css';
import pro360LogoFallback from '../../assets/images/pro360.png';

import { NavLink, Link, useLocation, useNavigate } from 'react-router-dom';
import { ReactComponent as Food } from '../../assets/images/svgIcons/Food.svg';
import { ReactComponent as FoodInactive } from '../../assets/images/svgIcons/Food_InActive.svg';
import { ReactComponent as CompleteOrder } from '../../assets/images/svgIcons/CompleteOrder.svg';
import { ReactComponent as PayIn } from '../../assets/images/svgIcons/in.svg';
import { ReactComponent as PayOut } from '../../assets/images/svgIcons/out.svg';

import ApiService from '../../services/apiService';
import userManagement from '../../services/userManagement';
import ReceiptModal from '../components/Reports/xReport';

import { FaChevronRight, FaUser, FaBars, FaGift, FaChartLine } from 'react-icons/fa';
import { FaChartBar, FaGifts, FaTruckArrowRight, FaTruckDroplet, FaTruckFieldUn, FaTruckRampBox } from 'react-icons/fa6';
import { HiOutlineDocumentReport } from 'react-icons/hi';

/** Inventory web (Issue/Received) — must not use React Router <Link to="https://..."> (resolves against POS host on live). */
const getInventoryWebRoot = () => String(process.env.REACT_APP_INVENTORY_WEB_URL || 'https://proc.pro360erp.com').replace(/\/+$/, '');

const SideBar = ({ id }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // ✅ normalize mode so it’s always defined
  const currentMode = location?.state?.mode || 'MENU';
  const isGiftCardMode = currentMode === 'GIFT_CARD';

  let token = localStorage.getItem('Token');
  let Permission = userManagement?.getUserCrud(token);

  const [categories, setCategories] = useState([]);
  const [TableEnable, setTableEnable] = useState();
  const [waiterPermission, setWaiterPermission] = useState();
  const [role, setRole] = useState();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const [companyLogo, setCompanyLogo] = useState(pro360LogoFallback);

  useEffect(() => {
    try {
      const raw = localStorage.getItem('userCompanies');
      const companies = raw ? JSON.parse(raw) : [];

      const logoUrl = companies?.[0]?.logo;
      if (logoUrl) setCompanyLogo(logoUrl);
    } catch (e) {
      // keep fallback if parsing fails
      setCompanyLogo(pro360LogoFallback);
    }
  }, []);
  const buildExternalUrlWithCredentials = (baseUrl) => {
    try {
      const raw = localStorage.getItem('LoginParams');
      if (!raw) return baseUrl;

      const loginParams = JSON.parse(raw);

      // Decode once before URLSearchParams — otherwise values already containing %2F get encoded again (%252F) and inventory SSO fails.
      const decodeParam = (v) => {
        if (v == null || v === '') return '';
        const s = String(v);
        try {
          return decodeURIComponent(s);
        } catch {
          return s;
        }
      };

      const query = new URLSearchParams({
        _username: decodeParam(loginParams.email),
        _password: decodeParam(loginParams.password),
        _db: decodeParam(loginParams.supportdb),
        _baseDb: decodeParam(loginParams.basedb),
      });

      const base = String(baseUrl || '').replace(/\?+$/, '');
      const sep = base.includes('?') ? '&' : '?';
      return `${base}${sep}${query.toString()}`;
    } catch (error) {
      console.error('Error building external URL:', error);
      return baseUrl;
    }
  };
  // ✅ loader on switch
  const [isSwitching, setIsSwitching] = useState(false);
  const fallbackTimerRef = useRef(null);

  useEffect(() => {
    let token = localStorage.getItem('Token');
    let Permission = userManagement?.getUserCrud(token);
    let role = userManagement?.getUserRole(token);
    setRole(role);

    const isTableEnable = Permission?.includes('Table Managment.add') && Permission.includes('Table Managment.query');
    const waiterPermission = Permission?.includes('Table Managment.add') && Permission.includes('Table Managment.query');

    setWaiterPermission(!waiterPermission);
    setTableEnable(!isTableEnable);

    GetCategory();
  }, []);

  useEffect(() => {
    const body = document.body;
    if (isCollapsed) body.classList.add('sidebar-collapsed');
    else body.classList.remove('sidebar-collapsed');
    return () => body.classList.remove('sidebar-collapsed');
  }, [isCollapsed]);

  // ✅ stop loader after mode/path changes
  useEffect(() => {
    if (!isSwitching) return;
    const t = setTimeout(() => setIsSwitching(false), 350);
    return () => clearTimeout(t);
  }, [location.pathname, location?.state?.mode, isSwitching]);

  useEffect(() => {
    return () => {
      if (fallbackTimerRef.current) clearTimeout(fallbackTimerRef.current);
    };
  }, []);

  const handleLinkClick = (e) => {
    e.preventDefault();
    setIsModalVisible(true);
  };

  const GetCategory = async () => {
    try {
      const apiService = new ApiService();
      let res = await apiService.get('GET_CATEGORY');
      const filteredcategory = res?.data?.result?.filter((item) => item?.active === true);
      setCategories(filteredcategory);
    } catch (error) {
      console.error('Error fetching category data:', error);
    }
  };

  const handleCollapse = () => setIsCollapsed((p) => !p);
  const handleMobileToggle = () => setIsMobileOpen((p) => !p);

  const primaryColor = '#6E5199';
  const secondaryColor = '#6E5199';
  const hoverColor = 'rgba(110, 81, 153, 0.1)';

  const mainNavItems = [
    {
      key: 'MENU',
      path: '/cashier/home',
      label: 'Menu',
      icon: Food,
      inactiveIcon: FoodInactive,
      state: { mode: 'MENU' }, // ✅ explicit
      forceNavigate: true,
    },
    {
      key: 'ORDERS',
      path: '/Cashier/SaleHistory',
      label: 'Orders',
      icon: CompleteOrder,
      inactiveIcon: CompleteOrder,
      state: null,
    },
  ];

  const inventoryRoot = useMemo(() => getInventoryWebRoot(), []);

  const cashierNavItems = useMemo(
    () => [
      {
        key: 'PAY_IN',
        path: '/Cashier/PayIn',
        label: 'Pay In',
        icon: PayIn,
        inactiveIcon: PayIn,
        state: null,
      },
      {
        key: 'PAY_OUT',
        path: '/Cashier/PayOut',
        label: 'Pay Out',
        icon: PayOut,
        inactiveIcon: PayOut,
        state: null,
      },
      {
        key: 'STOCK_IN',
        path: `${inventoryRoot}/Inventory/Issue/Received/`,
        label: 'Stock In',
        icon: FaTruckDroplet,
        inactiveIcon: FaTruckDroplet,
        external: true,
      },
      {
        key: 'STOCK_OUT',
        path: `${inventoryRoot}/Inventory/Issue/Direct/`,
        label: 'Stock Out',
        icon: FaTruckArrowRight,
        inactiveIcon: FaTruckArrowRight,
        external: true,
      },
    ],
    [inventoryRoot],
  );

  const reportsNavItems = [
    // {
    //   key: 'STOCK_REPORT',
    //   path: '/cashier/StockCheckByBranch',
    //   label: 'Stock Report',
    //   icon: FaTruckDroplet,
    //   inactiveIcon: FaTruckDroplet,
    //   state: null,
    // },
    {
      key: 'GIFT_CARD',
      path: '/cashier/home',
      label: 'Gift Card',
      state: { mode: 'GIFT_CARD' }, // ✅ explicit
      icon: FaGift,
      inactiveIcon: FaGift,
      forceNavigate: true,
    },
    {
      key: 'Check_GIFT_CARD',
      path: '/cashier/GiftCardCheck',
      label: 'Check Gift Card',
      icon: FaGifts,
      inactiveIcon: FaGifts,
      forceNavigate: true,
    },
    {
      key: 'Sale Summary Report',
      path: '/cashier/Reports/category',
      label: 'Sale Summary Report',
      icon: FaChartLine,
      inactiveIcon: FaChartLine,
      forceNavigate: true,
    },
    {
      key: 'Item Sale Report',
      path: '/Cashier/Reports/ItemSale',
      label: 'Item Sale Report',
      icon: FaChartBar,
      inactiveIcon: FaChartBar,
      forceNavigate: true,
    },
  ];

  const sidebarClasses = `modern-sidebar ${isCollapsed ? 'collapsed' : ''} ${isMobileOpen ? 'mobile-open' : ''}`;

  const sidebarStyle = {
    height: '100vh',
    background: 'var(--bg-color, #FFF)',
    boxShadow: '4px 0 20px rgba(110, 81, 153, 0.1)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'fixed',
    left: 0,
    top: 0,
    zIndex: 1000,
    display: 'flex',
    flexDirection: 'column',
    borderRight: `1px solid ${primaryColor}20`,
    width: isCollapsed ? '80px' : '280px',
  };

  const navSectionStyle = {
    flex: 1,
    padding: '24px 0',
    overflowY: 'auto',
    overflowX: 'hidden',
  };

  const navItemStyle = (isActive) => ({
    display: 'flex',
    alignItems: 'center',
    padding: isCollapsed ? '16px 20px' : '16px 24px',
    margin: isCollapsed ? '4px 8px' : '4px 16px',
    borderRadius: '12px',
    textDecoration: 'none',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    cursor: 'pointer',
    background: isActive ? `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)` : 'transparent',
    color: isActive ? '#FFFFFF' : 'var(--text-color, #3C4469)',
    boxShadow: isActive ? `0 4px 12px ${primaryColor}30` : 'none',
  });

  const iconContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    marginRight: isCollapsed ? '0' : '16px',
  };

  const labelStyle = (isActive) => ({
    fontSize: '14px',
    fontWeight: isActive ? '600' : '500',
    opacity: isCollapsed ? 0 : 1,
    transition: 'all 0.3s ease',
    whiteSpace: 'nowrap',
    color: 'inherit',
  });

  const sectionTitleStyle = {
    fontSize: '12px',
    fontWeight: '700',
    color: 'var(--subheading-color, #6c757d)',
    letterSpacing: '1px',
    textTransform: 'uppercase',
    padding: isCollapsed ? '0' : '0 24px',
    marginBottom: '12px',
    marginTop: '24px',
    opacity: isCollapsed ? 0 : 1,
  };

  const logoSectionStyle = {
    padding: isCollapsed ? '20px 10px' : '32px 24px',
    borderBottom: `1px solid ${primaryColor}15`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: isCollapsed ? 'center' : 'flex-start',
    minHeight: '80px',
  };

  const userSectionStyle = {
    padding: isCollapsed ? '16px 8px' : '20px 24px',
    borderTop: `1px solid ${primaryColor}15`,
    background: 'rgba(110, 81, 153, 0.05)',
  };

  // ✅ this is the critical active logic
  const isItemActive = (item) => {
    const samePath = location.pathname === item.path;

    // special: both Menu and Gift are on same path
    if (item.key === 'MENU') return samePath && currentMode !== 'GIFT_CARD';
    if (item.key === 'GIFT_CARD') return samePath && currentMode === 'GIFT_CARD';

    return samePath;
  };
  const switchRoute = (item) => {
    setIsMobileOpen(false);
    setIsSwitching(true);

    if (fallbackTimerRef.current) clearTimeout(fallbackTimerRef.current);
    fallbackTimerRef.current = setTimeout(() => setIsSwitching(false), 2000);

    navigate(item.path, { state: item.state ?? null });
  };
  const renderNavItem = (item) => {
    const active = isItemActive(item);

    // External absolute URLs must be real <a href> — NavLink's `to` is resolved against the POS app origin on live (wrong host / lost path).
    if (item.external) {
      const href = buildExternalUrlWithCredentials(item.path);
      return (
        <a
          key={item.key}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          style={navItemStyle(false)}
          className="nav-item-enhanced"
          onClick={() => setIsMobileOpen(false)}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = hoverColor;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
          }}
        >
          <div style={iconContainerStyle}>
            <item.inactiveIcon size={24} />
          </div>
          <span style={labelStyle(false)}>{item.label}</span>
        </a>
      );
    }

    // ✅ Menu + Gift Card share the same path, so don't use NavLink for them
    const isSharedRoute = item.key === 'MENU' || item.key === 'GIFT_CARD';
    const Component = isSharedRoute ? Link : NavLink;

    return (
      <Component
        key={item.key}
        to={item.path}
        state={item.state}
        style={navItemStyle(active)}
        className="nav-item-enhanced"
        onClick={(e) => {
          if (isSharedRoute || item.forceNavigate) {
            e.preventDefault();
            switchRoute(item);
            return;
          }
          setIsMobileOpen(false);
        }}
        onMouseEnter={(e) => {
          if (!active) e.currentTarget.style.background = hoverColor;
        }}
        onMouseLeave={(e) => {
          if (!active) e.currentTarget.style.background = 'transparent';
        }}
      >
        <div style={iconContainerStyle}>{active ? <item.icon size={24} /> : <item.inactiveIcon size={24} />}</div>
        <span style={labelStyle(active)}>{item.label}</span>
      </Component>
    );
  };

  return (
    <>
      {/* ✅ switching loader */}
      {isSwitching && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 5000,
            background: 'rgba(255,255,255,0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              background: 'white',
              borderRadius: 16,
              padding: '18px 22px',
              boxShadow: '0 10px 35px rgba(110,81,153,0.25)',
              border: `1px solid ${primaryColor}20`,
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              color: primaryColor,
              fontWeight: 600,
            }}
          >
            <div className="spinner-border" role="status" style={{ width: 22, height: 22 }}>
              <span className="visually-hidden">Loading...</span>
            </div>
            Loading...
          </div>
        </div>
      )}

      <div className={`sidebar-overlay ${isMobileOpen ? 'active' : ''}`} onClick={() => setIsMobileOpen(false)} />

      <button
        className="mobile-toggle"
        onClick={handleMobileToggle}
        style={{
          display: 'none',
          position: 'fixed',
          top: '20px',
          left: '20px',
          zIndex: 1001,
          background: primaryColor,
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          padding: '12px',
          cursor: 'pointer',
        }}
      >
        <FaBars size={16} />
      </button>

      <nav style={sidebarStyle} className={sidebarClasses}>
        <div style={logoSectionStyle}>
          <img
            src={companyLogo}
            alt="Logo"
            style={{
              height: isCollapsed ? '32px' : '39px',
              objectFit: 'contain',
            }}
            onError={(e) => {
              // if the URL fails to load, fall back
              e.currentTarget.src = pro360LogoFallback;
            }}
          />
        </div>

        <div style={navSectionStyle}>
          <div style={sectionTitleStyle}>Main Menu</div>
          {mainNavItems.map(renderNavItem)}

          {(role === 'Cashier' || role === 'Admin') && (
            <>
              <div style={sectionTitleStyle}>Cash Management</div>
              {cashierNavItems.map(renderNavItem)}
            </>
          )}

          <div style={sectionTitleStyle}>Reports</div>
          {reportsNavItems.map(renderNavItem)}

          <div
            onClick={(e) => {
              e.preventDefault();
              setIsModalVisible(true);
            }}
            style={navItemStyle(false)}
            className="nav-item-enhanced"
          >
            <div style={iconContainerStyle}>
              <HiOutlineDocumentReport size={24} />
            </div>
            <span style={labelStyle(false)}>X Report</span>
          </div>
        </div>

        <div style={userSectionStyle}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: isCollapsed ? 'center' : 'space-between' }}>
            {!isCollapsed && (
              <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                <div
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '12px',
                  }}
                >
                  <FaUser size={16} color="white" />
                </div>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: '600' }}>
                    {(() => {
                      const name = userManagement?.getUserName(token) || 'User';
                      return name.length > 16 ? name.slice(0, 16) + '..' : name;
                    })()}
                  </div>
                  <div style={{ fontSize: '12px', color: '#6c757d' }}>{role || 'Staff'}</div>
                </div>
              </div>
            )}

            <button
              onClick={() => setIsCollapsed((p) => !p)}
              style={{
                background: 'transparent',
                border: `1px solid ${primaryColor}30`,
                borderRadius: '8px',
                padding: '8px',
                color: primaryColor,
                cursor: 'pointer',
              }}
            >
              <FaChevronRight size={12} style={{ transform: isCollapsed ? 'rotate(0deg)' : 'rotate(180deg)' }} />
            </button>
          </div>
        </div>
      </nav>

      {isModalVisible && <ReceiptModal isVisible={isModalVisible} onClose={() => setIsModalVisible(false)} />}

      <style jsx>{`
        @media (max-width: 768px) {
          .mobile-toggle {
            display: block !important;
          }
        }
      `}</style>
    </>
  );
};

export default SideBar;
